/* newsletter-popup.js - newsletter popup with the -10% code (04-08, NEWS-01, NEWS-02).
   Markup: sections/newsletter-popup.liquid (<dialog id="NewsletterPopup">).

   Gating (form state, NEWS-01):
   - shown only after data-delay seconds on the page (default 15), never on entry;
   - never while the cookie banner waits for an answer: Shopify Customer Privacy API
     (Shopify.loadFeatures consent-tracking-api 0.1, shouldShowBanner() false, else the
     document event visitorConsentCollected). If the API is missing, the Consentmo
     banner (csm-cookie-consent open shadow root, .csm-cmp-bar) is checked every 2 s;
     while waiting for the event the same DOM check also counts (banner present but
     hidden = answered), in case Consentmo does not fire the Shopify event;
   - never while another dialog[open] (cart drawer, menu drawer, e-book consent) or
     #HeaderSearch[data-open] is open - re-checked every 2 s;
   - Liquid renders nothing on /cart, customers/*, password, gift_card and for
     logged-in subscribers (except right after the popup signup).
   Storage (flags only, no personal data):
   - localStorage  'zl-nl-popup'         = 'dismissed' (X / Esc / Zamknij) | 'subscribed'
   - sessionStorage 'zl-nl-popup-submit' = '1' between the popup submit and the reload
   - sessionStorage 'zl-nl-code-applied' = '1' once the code was sent to the cart
   - sessionStorage 'zl-nl-popup-shown'  = '1' the form state was shown in this session
     (ignored popup does not follow the visitor to every next page; next visit again).
   Success (NEWS-02, D-12, D-21): Shopify reloads after the customer form POST. Only if
   data-posted="success" AND the popup submit flag is present (Pitfall 4 - the footer
   form shares form.posted_successfully?) the popup opens at once in the success state,
   cloned from <template data-popup-success> (the code string exists only there), and
   the code is applied once with CartAPI.applyDiscount. "Kod dodaliśmy już do Twojego
   koszyka." is shown only when the returned cart lists the code with applicable true.
   textContent / importNode of own template only; never echoes form errors or the e-mail.

   Core: window.NewsletterPopup.create(deps) -> { start, dismiss, escape, submit, copy }
   deps { storage, session, setTimeout, clearTimeout, consent: { ready(cb) },
          overlaysOpen(), ui: { showForm, showSuccess, showError(kind), close, isOpen,
          setBusy(on), setApplied, setCopyLabel(kind), selectCode },
          applyDiscount(code) -> Promise<{ cart }>, clipboard, code, delayMs, applyCode,
          posted: 'success' | 'error' | null }
   window.NewsletterPopup.consentGate(env) -> { ready(cb) }, env { Shopify, document,
          setTimeout, clearTimeout }. window.NewsletterPopup.init() boots the DOM. */
(function () {
  'use strict';

  var KEY = 'zl-nl-popup';
  var KEY_SUBMIT = 'zl-nl-popup-submit';
  var KEY_APPLIED = 'zl-nl-code-applied';
  var KEY_SHOWN = 'zl-nl-popup-shown';
  var RECHECK_MS = 2000;
  var COPIED_MS = 2000;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function get(store, key) {
    try {
      return store ? store.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }

  function set(store, key, value) {
    try {
      if (store) store.setItem(key, value);
    } catch (e) {
      /* storage blocked: popup simply behaves per page */
    }
  }

  function remove(store, key) {
    try {
      if (store) store.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  }

  function codeApplicable(cart, code) {
    var list = cart && cart.discount_codes;
    var want = String(code).toLowerCase();
    if (!list || !list.length) return false;
    for (var i = 0; i < list.length; i++) {
      if (list[i] && String(list[i].code).toLowerCase() === want && list[i].applicable === true) return true;
    }
    return false;
  }

  function create(deps) {
    var ui = deps.ui;
    var code = deps.code ? String(deps.code).trim() : '';
    var timerDone = false;
    var consentDone = false;
    var shown = false;
    var started = false;
    var recheckId = null;
    var copyId = null;

    function tryShow() {
      recheckId = null;
      if (shown || !timerDone || !consentDone) return;
      if (deps.overlaysOpen()) {
        recheckId = deps.setTimeout(tryShow, RECHECK_MS);
        return;
      }
      shown = true;
      set(deps.session, KEY_SHOWN, '1');
      ui.showForm();
    }

    function applyCode() {
      if (!deps.applyCode || !code || !deps.applyDiscount) return;
      if (get(deps.session, KEY_APPLIED) === '1') return;
      set(deps.session, KEY_APPLIED, '1');
      var result;
      try {
        result = deps.applyDiscount(code);
      } catch (e) {
        return;
      }
      if (!result || typeof result.then !== 'function') return;
      result.then(function (res) {
        if (res && codeApplicable(res.cart, code)) ui.setApplied();
      }, function () {
        /* code not applied: the success state still shows the code to type in */
      });
    }

    function start() {
      if (started) return;
      started = true;
      var fromPopup = get(deps.session, KEY_SUBMIT) === '1';

      if (deps.posted === 'success' && fromPopup) {
        remove(deps.session, KEY_SUBMIT);
        set(deps.storage, KEY, 'subscribed');
        shown = true;
        ui.showSuccess();
        applyCode();
        return;
      }
      if (deps.posted === 'error' && fromPopup) {
        remove(deps.session, KEY_SUBMIT);
        shown = true;
        ui.showForm();
        ui.showError('generic');
        return;
      }
      /* another customer form (footer / home newsletter) was just posted: not now */
      if (deps.posted === 'success') return;

      var flag = get(deps.storage, KEY);
      if (flag === 'dismissed' || flag === 'subscribed') return;
      if (get(deps.session, KEY_SHOWN) === '1') return;

      deps.setTimeout(function () {
        timerDone = true;
        tryShow();
      }, deps.delayMs);
      deps.consent.ready(function () {
        if (consentDone) return;
        consentDone = true;
        if (timerDone && recheckId === null) tryShow();
      });
    }

    function dismiss() {
      if (recheckId !== null) {
        deps.clearTimeout(recheckId);
        recheckId = null;
      }
      ui.close();
      if (get(deps.storage, KEY) !== 'subscribed') set(deps.storage, KEY, 'dismissed');
    }

    function escape() {
      if (!ui.isOpen() || deps.overlaysOpen()) return false;
      dismiss();
      return true;
    }

    function submit(email) {
      var value = String(email == null ? '' : email).trim();
      if (!EMAIL_RE.test(value)) {
        ui.showError('invalid');
        return false;
      }
      set(deps.session, KEY_SUBMIT, '1');
      ui.setBusy(true);
      return true;
    }

    function fallback() {
      ui.selectCode();
      ui.setCopyLabel('fallback');
    }

    function copy() {
      var clip = deps.clipboard;
      if (copyId !== null) {
        deps.clearTimeout(copyId);
        copyId = null;
      }
      if (!code || !clip || typeof clip.writeText !== 'function') {
        fallback();
        return;
      }
      var p;
      try {
        p = clip.writeText(code);
      } catch (e) {
        fallback();
        return;
      }
      Promise.resolve(p).then(function () {
        ui.setCopyLabel('copied');
        copyId = deps.setTimeout(function () {
          copyId = null;
          ui.setCopyLabel('default');
        }, COPIED_MS);
      }, fallback);
    }

    return { start: start, dismiss: dismiss, escape: escape, submit: submit, copy: copy };
  }

  /* ---- consent gate ------------------------------------------------------ */

  function consentGate(env) {
    var doc = env.document;

    /* 'visible' | 'hidden' (bar rendered but not displayed = answered) | 'none' (no host / bar yet) */
    function bannerState() {
      var host = doc.querySelector('csm-cookie-consent');
      var root = host && host.shadowRoot;
      var bar = root && root.querySelector('.csm-cmp-bar');
      if (!bar) return 'none';
      return bar.getClientRects && bar.getClientRects().length ? 'visible' : 'hidden';
    }

    return {
      ready: function (cb) {
        var done = false;
        var pollId = null;

        function finish() {
          if (done) return;
          done = true;
          if (pollId !== null) env.clearTimeout(pollId);
          doc.removeEventListener('visitorConsentCollected', finish);
          cb();
        }

        function domPoll(strict) {
          pollId = null;
          if (done) return;
          var state = bannerState();
          if (state === 'hidden' || (strict && state === 'none')) {
            finish();
            return;
          }
          pollId = env.setTimeout(function () { domPoll(strict); }, RECHECK_MS);
        }

        function withApi() {
          var privacy = env.Shopify && env.Shopify.customerPrivacy;
          if (!privacy || typeof privacy.shouldShowBanner !== 'function') {
            domPoll(true);
            return;
          }
          var show = true;
          try {
            show = privacy.shouldShowBanner();
          } catch (e) {
            show = true;
          }
          if (!show) {
            finish();
            return;
          }
          doc.addEventListener('visitorConsentCollected', finish);
          domPoll(false);
        }

        var shop = env.Shopify;
        if (!shop || typeof shop.loadFeatures !== 'function') {
          domPoll(true);
          return;
        }
        try {
          shop.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
            if (error) domPoll(true);
            else withApi();
          });
        } catch (e) {
          domPoll(true);
        }
      }
    };
  }

  /* ---- DOM bootstrap ----------------------------------------------------- */

  function storageOf(name) {
    try {
      return window[name] || null;
    } catch (e) {
      return null;
    }
  }

  function init() {
    var doc = window.document;
    var dialog = doc.getElementById ? doc.getElementById('NewsletterPopup') : null;
    if (!dialog || dialog.getAttribute('data-ready') === 'true') return null;
    dialog.setAttribute('data-ready', 'true');

    var body = dialog.querySelector('[data-popup-body]');
    var form = dialog.querySelector('form');
    var input = dialog.querySelector('input[type="email"]');
    var submitBtn = dialog.querySelector('[data-popup-submit]');
    var errorBox = dialog.querySelector('[data-popup-error]');
    var errorText = dialog.querySelector('[data-popup-error-text]');
    var tpl = dialog.querySelector('template[data-popup-success]');
    var codeFromTpl = '';
    if (tpl && tpl.content) {
      var codeEl = tpl.content.querySelector('[data-popup-code]');
      if (codeEl) codeFromTpl = codeEl.textContent.trim();
    }
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hideTimer = null;
    var popup;

    function q(sel) {
      return dialog.querySelector(sel);
    }

    function open() {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      if (!dialog.open) {
        var prev = doc.activeElement;
        if (typeof dialog.show === 'function') dialog.show();
        else dialog.setAttribute('open', '');
        /* non-modal and no focus steal: give focus back to where the visitor was */
        if (prev && prev !== doc.body && typeof prev.focus === 'function') {
          try { prev.focus({ preventScroll: true }); } catch (e) { prev.focus(); }
        } else if (doc.activeElement && dialog.contains(doc.activeElement) && doc.activeElement.blur) {
          doc.activeElement.blur();
        }
      }
      if (reduce) {
        dialog.classList.add('is-visible');
      } else {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () { dialog.classList.add('is-visible'); });
        });
      }
    }

    function errorFor(kind) {
      return errorBox ? errorBox.getAttribute(kind === 'invalid' ? 'data-text-invalid' : 'data-text-generic') || '' : '';
    }

    var ui = {
      showForm: function () {
        open();
      },
      showSuccess: function () {
        if (tpl && body) {
          body.replaceChildren(doc.importNode(tpl.content, true));
          dialog.setAttribute('data-state', 'success');
          var copyBtn = q('[data-popup-copy]');
          if (copyBtn) copyBtn.addEventListener('click', function () { popup.copy(); });
          var closers = body.querySelectorAll('[data-popup-close]');
          for (var i = 0; i < closers.length; i++) {
            closers[i].addEventListener('click', onClose);
          }
        }
        open();
        var heading = q('[data-popup-success-heading]');
        if (heading) {
          try { heading.focus({ preventScroll: true }); } catch (e) { heading.focus(); }
        }
      },
      showError: function (kind) {
        if (!errorBox || !errorText) return;
        errorText.textContent = errorFor(kind);
        errorBox.hidden = false;
        if (input) input.setAttribute('aria-invalid', 'true');
      },
      close: function () {
        dialog.classList.remove('is-visible');
        if (reduce) {
          dialog.close();
          return;
        }
        hideTimer = setTimeout(function () {
          hideTimer = null;
          if (dialog.open) dialog.close();
        }, 300);
      },
      isOpen: function () {
        return !!dialog.open && hideTimer === null;
      },
      setBusy: function (on) {
        if (!submitBtn) return;
        var label = submitBtn.querySelector('[data-popup-submit-label]') || submitBtn;
        if (on) {
          submitBtn.setAttribute('data-label-idle', label.textContent);
          label.textContent = submitBtn.getAttribute('data-label-busy') || label.textContent;
          submitBtn.setAttribute('aria-disabled', 'true');
        } else {
          label.textContent = submitBtn.getAttribute('data-label-idle') || label.textContent;
          submitBtn.removeAttribute('aria-disabled');
        }
      },
      setApplied: function () {
        var line = q('[data-popup-applied]');
        if (line) line.hidden = false;
      },
      setCopyLabel: function (kind) {
        var btn = q('[data-popup-copy]');
        if (!btn) return;
        var label = btn.querySelector('[data-popup-copy-label]');
        var iconCopy = btn.querySelector('[data-icon-copy]');
        var iconCheck = btn.querySelector('[data-icon-check]');
        var text = btn.getAttribute(kind === 'copied' ? 'data-label-copied' : kind === 'fallback' ? 'data-label-fallback' : 'data-label-default');
        if (label && text) label.textContent = text;
        if (iconCopy) iconCopy.hidden = kind === 'copied';
        if (iconCheck) iconCheck.hidden = kind !== 'copied';
      },
      selectCode: function () {
        var el = q('[data-popup-code]');
        if (!el || !window.getSelection || !doc.createRange) return;
        var range = doc.createRange();
        range.selectNodeContents(el);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    };

    function overlaysOpen() {
      var open = doc.querySelectorAll('dialog[open]');
      for (var i = 0; i < open.length; i++) {
        if (open[i] !== dialog) return true;
      }
      return !!doc.querySelector('#HeaderSearch[data-open]');
    }

    var posted = dialog.getAttribute('data-posted');
    var delay = parseInt(dialog.getAttribute('data-delay'), 10);
    if (!(delay > 0)) delay = 15;

    popup = create({
      storage: storageOf('localStorage'),
      session: storageOf('sessionStorage'),
      setTimeout: function (fn, ms) { return window.setTimeout(fn, ms); },
      clearTimeout: function (id) { window.clearTimeout(id); },
      consent: consentGate({
        Shopify: window.Shopify,
        document: doc,
        setTimeout: function (fn, ms) { return window.setTimeout(fn, ms); },
        clearTimeout: function (id) { window.clearTimeout(id); }
      }),
      overlaysOpen: overlaysOpen,
      ui: ui,
      applyDiscount: function (c) {
        var api = window.CartAPI;
        if (!api || typeof api.applyDiscount !== 'function') return Promise.reject(new Error('no CartAPI'));
        return api.applyDiscount(c);
      },
      clipboard: window.navigator && window.navigator.clipboard,
      code: codeFromTpl,
      delayMs: delay * 1000,
      applyCode: dialog.getAttribute('data-apply-code') === 'true',
      posted: posted === 'success' || posted === 'error' ? posted : null
    });

    function onClose() {
      popup.dismiss();
    }

    var closeBtns = dialog.querySelectorAll('[data-popup-close]');
    for (var c = 0; c < closeBtns.length; c++) {
      closeBtns[c].addEventListener('click', onClose);
    }

    doc.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (popup.escape()) event.preventDefault();
    });

    /* native dialog cancel (some browsers fire it on Esc for non-modal dialogs too) */
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      popup.escape();
    });

    if (form) {
      form.addEventListener('submit', function (event) {
        if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        if (!popup.submit(input ? input.value : '')) {
          event.preventDefault();
          if (input) input.focus();
        }
      });
    }
    if (input) {
      input.addEventListener('input', function () {
        if (input.getAttribute('aria-invalid') === 'true') {
          input.removeAttribute('aria-invalid');
          if (errorBox) errorBox.hidden = true;
        }
      });
    }

    popup.start();
    return popup;
  }

  window.NewsletterPopup = { create: create, consentGate: consentGate, init: init };

  var d = window.document;
  if (d && d.getElementById) {
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init);
    else init();
  }
})();
